import React, { useEffect, useRef, useCallback, useMemo } from "react";
import { Modal, ModalBody } from "reactstrap";
import { useCallContext } from "../../../store/context/CallProvider";
import { useSocket } from "../../../store/context/SocketProvider";
import sound from "../../../assets/sounds/Ringing.mp3";

const CallModal = React.memo((props) => {
  const {
    isOpen,
    toggleCallModal,
    isVideo,
    user,
    inComming,
    currentUser,
    conversationId,
    updateMessages,
  } = props;

  const audioRef = useRef(null);
  const { callUser, declineCall, answerCall } = useCallContext();
  const { socket } = useSocket();

  // Memoized user display info
  const userDisplay = useMemo(() => ({
    name: user?.isGroupChat 
      ? user.name 
      : `${user?.contactName?.first} ${user?.contactName?.last}`,
    image: user?.isGroupChat
      ? "https://static.vecteezy.com/system/resources/thumbnails/021/063/599/small/group-coaching-icon-vector.jpg"
      : user?.imageSrc
  }), [user]);

  // Stable audio control functions
  const controlAudio = useCallback((action = 'stop') => {
    if (!audioRef.current) return;
    
    if (action === 'play') {
      audioRef.current.volume = inComming ? 1 : 0.3;
      audioRef.current.loop = true;
      audioRef.current.play();
    } else {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [inComming]);

  // Handle call initialization
  const handleCall = useCallback((type) => {
    controlAudio('play');
    callUser(user, type === 'video', currentUser, conversationId);
  }, [callUser, controlAudio, conversationId, currentUser, user]);

  // Handle call decline/close
  const handleDecline = useCallback(() => {
    controlAudio('stop');
    declineCall();
    toggleCallModal();
  }, [controlAudio, declineCall, toggleCallModal]);

  // Handle call answer
  const handleAnswer = useCallback(() => {
    controlAudio('stop');
    answerCall();
  }, [answerCall, controlAudio]);

  // Audio effect management
  useEffect(() => {
    audioRef.current = new Audio(sound);
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Incoming call audio control
  useEffect(() => {
    if (inComming && isOpen) {
      controlAudio('play');
    } else {
      controlAudio('stop');
    }
  }, [inComming, isOpen, controlAudio]);

  // Call status tracking
  const callStatus = useMemo(() => {
    if (inComming) return "Calling you";
    if (isVideo) return "Start Video Call";
    return "Start Audio Call";
  }, [inComming, isVideo]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggleCallModal}
      centered
      onClosed={controlAudio.bind(null, 'stop')}
    >
      <ModalBody>
        <div className="text-center p-4">
          <div className="avatar-md mx-auto mb-4">
            <img
              src={userDisplay.image}
              alt=""
              className="img-thumbnail rounded-circle"
            />
          </div>

          <h5 className="text-truncate">{userDisplay.name}</h5>
          <p className="text-muted">{callStatus}</p>

          <div className="mt-5">
            <ul className="list-inline mb-1">
              <li className="list-inline-item px-2 me-2 ms-0">
                <button
                  type="button"
                  className="btn btn-danger avatar-sm rounded-circle"
                  onClick={handleDecline}
                >
                  <span className="avatar-title bg-transparent font-size-20">
                    <i className="ri-close-fill"></i>
                  </span>
                </button>
              </li>
              {inComming ? (
                <li className="list-inline-item px-2">
                  <button
                    type="button"
                    className="btn btn-success avatar-sm rounded-circle"
                    onClick={handleAnswer}
                  >
                    <span className="avatar-title bg-transparent font-size-20">
                      <i className={`ri-${isVideo ? 'vidicon' : 'phone'}-fill`}></i>
                    </span>
                  </button>
                </li>
              ) : (
                <li className="list-inline-item px-2">
                  <button
                    type="button"
                    className="btn btn-success avatar-sm rounded-circle"
                    onClick={() => handleCall(isVideo ? 'video' : 'audio')}
                  >
                    <span className="avatar-title bg-transparent font-size-20">
                      <i className={`ri-${isVideo ? 'vidicon' : 'phone'}-fill`}></i>
                    </span>
                  </button>
                </li>
              )}
            </ul>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for React.memo
  return prevProps.isOpen === nextProps.isOpen && 
         prevProps.isVideo === nextProps.isVideo &&
         prevProps.inComming === nextProps.inComming &&
         prevProps.user?._id === nextProps.user?._id &&
         prevProps.currentUser?._id === nextProps.currentUser?._id;
});

export default CallModal;